import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useApp, formatINR, optimizedImage } from "../store";
import useDocumentMeta from "../hooks/useDocumentMeta";

export default function Cart() {
  useDocumentMeta({
    title: "Your Cart",
    description: "Review the floral absolutes, attars, and oils in your cart before checkout at House of Absolutes KA.",
  });
  const { cart, updateQty, removeFromCart, settings } = useApp();
  const navigate = useNavigate();
  const subtotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);
  const gst = settings?.gst_rate ?? 0.18;
  const threshold = settings?.free_shipping_threshold ?? 5000;
  const flat = settings?.shipping_flat_rate ?? 250;
  const tax = Math.round(subtotal * gst);
  const shipping = subtotal >= threshold ? 0 : flat;
  const total = subtotal + tax + shipping;

  if (cart.length === 0) {
    return (
      <div className="pt-40 pb-24 px-6 md:px-12 max-w-2xl mx-auto text-center" data-testid="empty-cart">
        <div className="overline text-copper mb-4">The Cart</div>
        <h1 className="font-serif-display text-6xl mb-6">Empty<span className="italic text-[#8A8D8A]">, for now.</span></h1>
        <p className="text-[#5C5E5C] mb-10">You have not added any essences to your cart yet.</p>
        <Link to="/catalog" className="btn-primary">Browse the catalog</Link>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-[1500px] mx-auto" data-testid="cart-page">
      <div className="overline text-copper mb-4">Your Selection</div>
      <h1 className="headline-xl text-[clamp(3rem,6vw,6rem)] mb-16">The Cart<span className="italic text-[#8A8D8A]">.</span></h1>

      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 divide-y divide-[#E5E1D8] border-y border-[#E5E1D8]">
          {cart.map(item => (
            <div key={`${item.product_id}-${item.size}`} className="flex gap-6 py-8" data-testid={`cart-item-${item.product_id}-${item.size}`}>
              <Link to={`/product/${item.product_id}`} className="w-28 h-32 bg-clay overflow-hidden flex-shrink-0">
                <img src={optimizedImage(item.image_url, 200)} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <Link to={`/product/${item.product_id}`} className="font-serif-display text-2xl hover:text-copper">{item.name}</Link>
                    <div className="overline text-[10px] text-[#8A8D8A] mt-1">Pack · {item.size}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif-display text-xl">{formatINR(item.price * item.quantity)}</div>
                    <div className="text-xs text-[#8A8D8A]">{formatINR(item.price)} ea</div>
                  </div>
                </div>
                <div className="mt-auto flex justify-between items-center">
                  <div className="flex items-center border border-[#E5E1D8]">
                    <button onClick={() => updateQty(item.product_id, item.size, item.quantity - 1)} className="px-2 py-1 hover:bg-[#F3EFE9]"><Minus size={12} /></button>
                    <span className="px-3 text-sm">{item.quantity}</span>
                    <button onClick={() => updateQty(item.product_id, item.size, item.quantity + 1)} className="px-2 py-1 hover:bg-[#F3EFE9]"><Plus size={12} /></button>
                  </div>
                  <button onClick={() => removeFromCart(item.product_id, item.size)} className="text-xs text-[#8A8D8A] hover:text-[#9E3C3C] flex items-center gap-1" data-testid={`remove-${item.product_id}-${item.size}`}>
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-32 bg-sand p-8 space-y-5">
            <div className="overline text-copper">Order Summary</div>
            <div className="space-y-3 text-sm">
              <Row label="Subtotal" value={formatINR(subtotal)} />
              {gst > 0 && <Row label={`GST (${Math.round(gst * 100)}%)`} value={formatINR(tax)} />}
              <Row label="Shipping" value={shipping === 0 ? "Free" : formatINR(shipping)} />
              {shipping > 0 && <p className="text-[10px] text-[#8A8D8A]">Free shipping over {formatINR(threshold)}</p>}
            </div>
            <div className="border-t border-[#E5E1D8] pt-4 flex justify-between items-baseline">
              <span className="overline">Total</span>
              <span className="font-serif-display text-3xl" data-testid="cart-total">{formatINR(total)}</span>
            </div>
            <button onClick={() => navigate("/checkout")} className="btn-primary w-full justify-center" data-testid="checkout-cta">
              Proceed to checkout
            </button>
            <Link to="/catalog" className="block text-center text-xs text-[#8A8D8A] hover:text-copper">Continue shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#5C5E5C]">{label}</span>
      <span>{value}</span>
    </div>
  );
}
